import React, { useState } from 'react';
import { Table, Input, Button, InputNumber, Space } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchProcessingFeeToggleVisibility } from '../../treasury/api';
import { recalcGoldItem, roundAmount } from '../../../utils/goldCalculation';

const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(price);
};

const formatWholeAmount = (price) => {
    return new Intl.NumberFormat('en-IN', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(price);
};

const GoldItemsTable = ({ ratePerGram, goldDetails, onChange, showDeleteOption, processingFees = 0, onProcessingFeesChange }) => {
    const { canViewField, canEditField } = usePermissions();
    const { user } = useAuth();
    const module = 'billing';
    const [processingFee, setProcessingFee] = useState(0.5);
    const [showProcessingFeeToggle, setShowProcessingFeeToggle] = useState(true);

    React.useEffect(() => {
        if (user?.branch) {
            fetchBranchProcessingFeeToggleVisibility(user.branch)
                .then(res => {
                    if (res?.data?.data?.isVisible !== undefined) {
                        setShowProcessingFeeToggle(res.data.data.isVisible);
                    }
                })
                .catch(err => console.error('Failed to fetch Processing Fee visibility:', err));
        }
    }, [user]);

    const totalAmount = goldDetails.reduce((sum, item) => sum + (item.totalPrice || 0), 0);

    React.useEffect(() => {
        if (onProcessingFeesChange) {
            const effectiveProcessingFee = showProcessingFeeToggle ? processingFee : 0;
            const pFeeVal = roundAmount(totalAmount * effectiveProcessingFee / 100);
            onProcessingFeesChange(pFeeVal);
        }
    }, [totalAmount, processingFee, showProcessingFeeToggle, onProcessingFeesChange]);

    const handleChange = (index, field, value) => {
        const updatedDetails = [...goldDetails];
        updatedDetails[index][field] = value;
        
        // If dustDeduction is being manually changed, mark it as overridden
        // Otherwise, let it auto-recalculate
        const isDustDeductionChange = field === 'dustDeduction';
        const skipDustRecalc = isDustDeductionChange;
        
        // If a non-dustDeduction field changes, clear the override flag
        if (!isDustDeductionChange) {
            updatedDetails[index]._dustDeductionOverride = false;
        } else {
            updatedDetails[index]._dustDeductionOverride = true;
        }
        
        updatedDetails[index] = recalcGoldItem(updatedDetails[index], { skipDustRecalc });
        onChange(updatedDetails);
    };

    const handleAddItem = () => {
        const newItem = {
            itemName: '',
            purity: 0,
            netWeight: 0,
            grossWeight: 0,
            deduction: 0,
            dustDeduction: 0,
            ratePerGram: ratePerGram,
            quantity: goldDetails.length + 1,
            totalPrice: 0,
        };
        onChange([...goldDetails, newItem]);
    };

    const handleRemoveItem = (index) => {
        if (goldDetails.length === 1) return;
        const updatedDetails = goldDetails.filter((_, i) => i !== index);
        updatedDetails.forEach((item, idx) => (item.quantity = idx + 1));
        onChange(updatedDetails);
    };



    const renderInputIfAllowed = (field, render) => {
        return canViewField(module, field) ? render() : null;
    };

    const isDisabled = (field) => !canEditField(module, field);

    const columns = [
        {
            title: <span style={{ fontSize: '10px' }}>Qty</span>,
            width: '5%',
            render: (_, __, index) => (
                <Space align="center" size="small" style={{ display: 'flex' }}>
                    {goldDetails.length > 1 && (
                        <Button
                            className="no-print"
                            type="text"
                            danger
                            icon={<CloseOutlined />}
                            onClick={() => handleRemoveItem(index)}
                            size="small"
                            style={{ padding: 0, marginRight: 4 , fontSize: '10px'}}
                        />
                    )}
                    <div style={{ minWidth: 30 }}>{index + 1}</div>
                </Space>
            ),
            align: 'left',
        },
        {
            title: <span style={{ fontSize: '10px' }}>Item Name</span>,
            dataIndex: 'itemName',
            width: '20%',
            render: (text, _, index) =>
                renderInputIfAllowed('itemName', () => (
                    <Input.TextArea
                        autoSize={{ minRows: 2, maxRows: 3 }}
                        value={text}
                        onChange={(e) => handleChange(index, 'itemName', e.target.value)}
                        size="small"
                        style={{ fontSize: '10px'}}
                        disabled={isDisabled('itemName')}
                    />
                )),
        },
        {
            title: <span style={{ fontSize: '10px' }}>Purity (%)</span>,
            dataIndex: 'purity',
            width: '9%',
            render: (value, _, index) =>
                renderInputIfAllowed('purity', () => (
                    <InputNumber
                        min={0}
                        max={100}
                        step={1}
                        precision={2}
                        value={value}
                        onChange={(val) => handleChange(index, 'purity', val)}
                        size="small"
                        style={{ width: '100%', fontSize: '10px' }}
                        disabled={isDisabled('purity')}
                        controls={false}
                        placeholder="e.g. 90"
                    />
                )),
        },
        {
            title: <span style={{ fontSize: '10px' }}>Gross Weight (gm)</span>,
            dataIndex: 'grossWeight',
            width: '10%',
            render: (value, _, index) =>
                renderInputIfAllowed('grossWeight', () => (
                    <InputNumber
                        min={0}
                        step={0.01}
                        precision={2}
                        value={value}
                        onChange={(val) => handleChange(index, 'grossWeight', val)}
                        size="small"
                        style={{ width: '100%', fontSize: '10px' }}
                        disabled={isDisabled('grossWeight')}
                        controls={false}
                    />
                )),
        },
        {
            title: <span style={{ fontSize: '10px' }}>Stone/wax Deduction (gm)</span>,
            dataIndex: 'deduction',
            width: '8%',
            render: (value, _, index) =>
                renderInputIfAllowed('deduction', () => (
                    <InputNumber
                        min={0}
                        value={value}
                        onChange={(val) => handleChange(index, 'deduction', val)}
                        size="small"
                        style={{ width: '100%', fontSize: '10px' }}
                        disabled={isDisabled('deduction')}
                        controls={false}
                    />
                )),
        },
        {
            title: <span style={{ fontSize: '10px' }}>Dust Deduction (gm)</span>,
            dataIndex: 'dustDeduction',
            width: '8%',
            render: (value, _, index) =>
                renderInputIfAllowed('dustDeduction', () => (
                    <InputNumber
                        min={0}
                        value={value}
                        onChange={(val) => handleChange(index, 'dustDeduction', val)}
                        size="small"
                        style={{ width: '100%', fontSize: '10px' }}
                        disabled={isDisabled('dustDeduction')}
                        controls={false}
                    />
                )),
        },
        {
            title: <span style={{ fontSize: '10px' }}>Net Weight (gm)</span>,
            dataIndex: 'netWeight',
            width: '10%',
            render: (value) => (
                <InputNumber
                    value={value}
                    size="small"
                    disabled
                    style={{
                        width: '100%',
                        fontSize: '10px',
                        backgroundColor: 'white',
                        color: 'black',
                        borderColor: '#d9d9d9',
                    }}
                />
            ),
        },
        {
            title: <span style={{ fontSize: '10px' }}>Rate / gm (₹)</span>,
            dataIndex: 'ratePerGram',
            width: '13%',
            render: (value, _, index) =>
                renderInputIfAllowed('ratePerGram', () => (
                    <InputNumber
                        min={0}
                        max={100000}
                        value={value}
                        onChange={(val) => handleChange(index, 'ratePerGram', val)}
                        size="small"
                        style={{
                            width: '100%',
                            fontSize: '10px',
                            backgroundColor: 'white',
                            color: 'black',
                            borderColor: '#d9d9d9',
                            opacity: 1, // Override default disabled opacity
                            cursor: 'not-allowed' // Optional: indicate non-editable
                        }}
                        disabled={isDisabled('ratePerGram')}
                        controls={false}
                    />
                )),
        },
        {
            title: <span style={{ fontSize: '10px' }}>Total Price (₹)</span>,
            dataIndex: 'totalPrice',
            width: '130%',
            fontSize: '12px',
            render: (value) => <strong>₹ {formatPrice(value || 0)}</strong>,
        },
    ];

    return (
        <>
            <Table
                dataSource={goldDetails.map((item, index) => ({ ...item, key: index }))}
                columns={columns}
                pagination={false}
                size="small"
                bordered
                summary={() => {
                    const effectiveProcessingFee = showProcessingFeeToggle ? processingFee : 0;
                    // P fee = Total Gold Amount * processingFee / 100
                    const pFee = roundAmount(totalAmount * effectiveProcessingFee / 100);
                    const finalAmount = roundAmount(totalAmount - pFee);

                    return (
                        <>
                            <Table.Summary.Row>
                                <Table.Summary.Cell colSpan={columns.length - 1}  align="right">
                                    <strong style={{ fontSize: '12px' }}>Total Gold Amount</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell>
                                    <strong style={{ fontSize: '12px' }}>₹ {formatPrice(totalAmount)}</strong>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                            {showProcessingFeeToggle && (
                            <Table.Summary.Row>
                                <Table.Summary.Cell colSpan={columns.length - 1} align="right">
                                    <Space>
                                        <strong style={{ fontSize: '12px' }}>Processing Fee (%)</strong>
                                            <InputNumber
                                                min={0}
                                                max={100}
                                                step={0.1}
                                                precision={2}
                                                value={processingFee}
                                                onChange={(val) => {
                                                    setProcessingFee(val || 0);
                                                    if (onProcessingFeesChange) {
                                                        // Pass the computed absolute fee to parent if needed
                                                        const pFeeVal = roundAmount(totalAmount * (val || 0) / 100);
                                                        onProcessingFeesChange(pFeeVal);
                                                    }
                                                }}
                                                size="small"
                                                style={{ width: 80, fontSize: '10px' }}
                                                controls={false}
                                            />
                                    </Space>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell>
                                    <strong style={{ fontSize: '12px' }}>-₹ {formatWholeAmount(pFee)}</strong>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                            )}
                            <Table.Summary.Row>
                                <Table.Summary.Cell colSpan={columns.length - 1} align="right">
                                    <strong style={{ fontSize: '12px' }}>Final Amount</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell>
                                    <strong style={{ fontSize: '12px' }}>₹ {formatWholeAmount(finalAmount)}</strong>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </>
                    );
                }}
            />
            {showDeleteOption && (
                <Button className="no-print" type="primary" onClick={handleAddItem} style={{ marginTop: 16 }}>
                    Add Gold Item
                </Button>
            )}
        </>
    );
};

export default GoldItemsTable;
